import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { publicRequest, privateRequest } from "../utils/api";
import { useSelector } from "react-redux";
import type { RootState } from "../store";

export interface BlogTranslation {
  id?: number;
  locale: string;
  title: string;
  excerpt?: string;
  body: string;
  slug: string;
  author_title?: string;
  author_bio?: string;
}

export interface BlogPost {
  id: string;
  title: string;
  excerpt?: string;
  body: string;
  slug: string;
  featured_image?: string;
  read_time?: number;
  status: "draft" | "review" | "published" | "archived";
  is_featured: boolean;
  author?: string;
  author_id?: number;
  author_name?: string;
  author_picture?: string;
  author_image?: string;
  author_title?: string;
  author_bio?: string;
  categories?: string[];
  tags?: string[];
  published_at?: string;
  created_at?: string;
  updated_at?: string;
  translations?: BlogTranslation[];
}

export interface BlogsResponse {
  posts: BlogPost[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface BlogsQueryParams {
  per_page?: number;
  page?: number;
  locale?: string;
  search?: string;
  status?: string;
  category?: string;
  featured?: boolean;
  admin?: boolean;
}

export interface CreateBlogData {
  status: "draft" | "review" | "published" | "archived";
  is_featured?: boolean;
  author_id?: number;
  author_name?: string;
  author_picture?: string;
  featured_image?: string;
  read_time?: number;
  published_at?: string;
  categories?: string[];
  tags?: string[];
  translations: BlogTranslation[];
}

export type UpdateBlogData = Partial<CreateBlogData>;

export const BLOGS_QUERY_KEY = ["blogs"] as const;
export const BLOG_QUERY_KEY = (id: number) => ["blogs", id] as const;

// Fetch blogs with pagination
const fetchBlogs = async (
  params: BlogsQueryParams = {}
): Promise<BlogsResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.per_page)
      queryParams.append("per_page", params.per_page.toString());
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.locale) queryParams.append("locale", params.locale);
    if (params.search) queryParams.append("search", params.search);
    if (params.status) queryParams.append("status", params.status);
    if (params.category) queryParams.append("category", params.category);
    if (params.featured !== undefined)
      queryParams.append("featured", params.featured.toString());
    if (params.admin) queryParams.append("admin", "true");

    const response = await publicRequest.get(`/posts?${queryParams}`);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching blogs:", error);
    throw error;
  }
};

// Fetch single blog
const fetchBlog = async (
  id: number,
  admin: boolean = false
): Promise<BlogPost> => {
  try {
    const params = admin ? "?admin=true" : "";
    const response = await publicRequest.get(`/posts/${id}${params}`);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching blog:", error);
    throw error;
  }
};

// Create blog
const createBlog = async (blogData: CreateBlogData): Promise<BlogPost> => {
  const response = await privateRequest.post("/posts", blogData);
  return response.data.data.post;
};

// Update blog
const updateBlog = async (
  id: number,
  blogData: UpdateBlogData
): Promise<BlogPost> => {
  const response = await privateRequest.put(`/posts/${id}`, blogData);
  return response.data.data.post;
};

// Delete blog
const deleteBlog = async (id: number): Promise<void> => {
  await privateRequest.delete(`/posts/${id}`);
};

// Custom hooks
export const useBlogs = (params: BlogsQueryParams = {}) => {
  const { currentLanguage } = useSelector((state: RootState) => state.language);

  const result = useQuery({
    queryKey: [
      ...BLOGS_QUERY_KEY,
      { ...params, locale: params.locale || currentLanguage },
    ],
    queryFn: () =>
      fetchBlogs({ ...params, locale: params.locale || currentLanguage }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return result;
};

// Admin hook that includes all translations
export const useBlogsAdmin = (params: BlogsQueryParams = {}) => {
  return useQuery({
    queryKey: [...BLOGS_QUERY_KEY, "admin", params],
    queryFn: () => fetchBlogs({ ...params, admin: true }),
    staleTime: 0, // Always refetch on mount for admin
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useBlog = (
  id: number,
  admin: boolean = false,
  enabled: boolean = true
) => {
  const { currentLanguage } = useSelector((state: RootState) => state.language);

  return useQuery({
    queryKey: [...BLOG_QUERY_KEY(id), admin, currentLanguage],
    queryFn: () => fetchBlog(id, admin),
    enabled: enabled && id > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useCreateBlog = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createBlog,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BLOGS_QUERY_KEY });
    },
  });
};

export const useUpdateBlog = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, blogData }: { id: number; blogData: UpdateBlogData }) =>
      updateBlog(id, blogData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: BLOGS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: BLOG_QUERY_KEY(Number(data.id)),
      });
    },
  });
};

export const useDeleteBlog = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteBlog,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BLOGS_QUERY_KEY });
    },
  });
};
